import { z } from 'zod';

/**
 * Mirrors `apps/workspace-api/src/briefs/section-value/section-value.schema.ts` — same seven
 * per-type shapes, same config refinements. Pinned equal by `sectionValue.pin.test.ts`, exactly
 * as `sectionTypes.test.ts` pins `DEFAULT_SECTION_CONFIG` against the backend's copy. Diverges
 * from the backend in one deliberate way: this never throws. A malformed value from the API
 * (or a race with a concurrent edit) must not crash the section's render — every API response
 * is parsed through this before a widget sees it, and a failed parse degrades to "no value"
 * rather than breaking the page.
 */

/** The only `config.format` that offers a second date input. */
// eslint-disable-next-line i18next/no-literal-string -- persisted config value, agreed with the server, not copy
const RANGE_DATE_FORMAT = 'Start + End date';

const CALENDAR_DATE_PREFIX = /^(\d{4})-(\d{2})-(\d{2})/;

/**
 * A date is persisted as a string, so the shape alone cannot tell a date from a sentence. An
 * agent proposing "next quarter" satisfied `z.string()`, and the unparseable value reached the
 * widget, where formatting an Invalid Date threw and blanked the page.
 *
 * Mere parseability is too loose to be the rule, because this side is where a value is written
 * rather than only read. `parseISO` accepts partial and exotic ISO forms the server refuses —
 * '2026-09', '20260101', '2026-W01-1' — so accepting one here writes it, resolves the AI
 * suggestion that proposed it as applied, and then 400s on every later save of that brief,
 * rolling back the whole PATCH including the title. A brief date is a day the briefer picks in
 * a DatePicker, so the rule both sides hold is a full year-month-day that the month really has.
 * A trailing time is tolerated because stored values already carry one.
 *
 * Kept equal to `apps/workspace-api/src/briefs/section-value/section-value.schema.ts` by the
 * pinned table in `sectionValue.pin.test.ts`.
 */
function isCalendarDate(value: string): boolean {
  const match = CALENDAR_DATE_PREFIX.exec(value);
  if (!match || Number.isNaN(Date.parse(value))) return false;

  const [, year, month, day] = match;
  const roundTripped = new Date(`${year}-${month}-${day}T00:00:00Z`);
  return (
    roundTripped.getUTCFullYear() === Number(year) &&
    roundTripped.getUTCMonth() + 1 === Number(month) &&
    roundTripped.getUTCDate() === Number(day)
  );
}

const isoDateString = z.string().refine(isCalendarDate);

const dateValueSchema = z.object({
  start: isoDateString.nullable(),
  end: isoDateString.nullable(),
});

/** Shared by `select` and `checklist` — both persist a list of selected option strings. */
const optionsValueSchema = z.object({
  selected: z.array(z.string()),
});

const urlValueSchema = z.object({
  url: z.string().nullable(),
});

const fileValueSchema = z.object({
  assetIds: z.array(z.string()),
});

const booleanValueSchema = z.object({
  value: z.boolean().nullable(),
});

export interface ParseSectionValueResult {
  success: boolean;
  data: unknown;
}

const ok = (data: unknown): ParseSectionValueResult => ({ success: true, data });
const fail: ParseSectionValueResult = { success: false, data: null };

export function parseSectionValue(
  type: string | null | undefined,
  value: unknown,
  config: Record<string, unknown> = {},
): ParseSectionValueResult {
  if (type == null || type === 'text') return ok(null);
  if (value == null) return ok(null);

  switch (type) {
    case 'date': {
      const parsed = dateValueSchema.safeParse(value);
      if (!parsed.success) return fail;
      // Fails CLOSED, matching the widget: only the range format offers a second input, so
      // an absent or unrecognised format means a single date on both sides. Keying off
      // "not start-only" instead accepted an `end` the briefer had no way to enter.
      if (config.format !== RANGE_DATE_FORMAT && parsed.data.end !== null) return fail;
      return ok(parsed.data);
    }
    case 'select':
    case 'checklist': {
      const parsed = optionsValueSchema.safeParse(value);
      if (!parsed.success) return fail;
      if (type === 'select' && config.multiple === false && parsed.data.selected.length > 1) {
        return fail;
      }
      return ok(parsed.data);
    }
    case 'url': {
      const parsed = urlValueSchema.safeParse(value);
      return parsed.success ? ok(parsed.data) : fail;
    }
    case 'file': {
      const parsed = fileValueSchema.safeParse(value);
      return parsed.success ? ok(parsed.data) : fail;
    }
    case 'boolean': {
      const parsed = booleanValueSchema.safeParse(value);
      return parsed.success ? ok(parsed.data) : fail;
    }
    default:
      // An unrecognized type is not this boundary's job to reject — pass it through opaque.
      return ok(value);
  }
}
